import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { errorMessage } from "@/app/api";
import { useCanWrite } from "@/app/auth";
import { CAPTURE_STATE, captures, captureTarget, formatClock, hexDump, pduSummary, type CapturedPDU } from "@/app/captures";
import { accounts, channels } from "@/app/master";
import { Pager } from "@/app/Pager";
import { useList } from "@/app/queries";
import { ReadonlyBlock } from "@/app/ReadonlyBlock";
import { usePage } from "@/app/usePage";
import { Alert, Button, Drawer, EmptyState, formatTime, Input as TextInput, KeyValue, PageHeader, Pill, SectionTitle, Table, TableWrap, Th, Toolbar, ToolbarCount } from "@/ui";
import { StopModal } from "./Captures";

/** 抓包详情页 /captures/{id}（决策 D93）：按时间线列出抓到的 PDU，进行中的任务每 2 秒追加新的；点"详情"看解码字段与十六进制原文。 */
export function CaptureDetail() {
  const { id = "" } = useParams();
  const captureId = /^\d+$/.test(id) ? Number(id) : 0;
  const navigate = useNavigate();
  const canWrite = useCanWrite();
  const [stopping, setStopping] = useState(false);
  const [open, setOpen] = useState<CapturedPDU | null>(null);
  const [q, setQ] = useState("");
  const cap = useQuery({ queryKey: ["captures", captureId], queryFn: () => captures.get(captureId), enabled: captureId > 0,
    refetchInterval: (query) => (query.state.data?.state === "running" ? 3_000 : false) });
  const target = captureTarget(useList("accounts", accounts).data, useList("channels", channels).data);
  const packets = usePackets(cap.data ? captureId : 0, cap.data?.state === "running"); // 任务读到了再拉，免得加载前后各拉一遍
  const keyword = q.trim().toLowerCase();
  const shown = keyword ? packets.items.filter((p) => `${p.command} ${p.status_name} ${pduSummary(p)}`.toLowerCase().includes(keyword)) : packets.items;
  const paged = usePage("capture-packets", shown, keyword);
  const c = cap.data;
  const back = <Button onClick={() => navigate("/captures")}>返回</Button>;

  if (!c) {
    return (
      <>
        <PageHeader title="抓包调试" actions={back} />
        {!captureId || cap.error ? <Alert tone="danger" title="加载失败">{captureId ? cap.error?.message : `任务标识 ${id} 无效`}</Alert> : <TableWrap loading />}
      </>
    );
  }
  return (
    <>
      <PageHeader title={<>抓包 #{c.id} {target(c)} <Pill tone={CAPTURE_STATE[c.state].tone}>{CAPTURE_STATE[c.state].label}</Pill></>}
        description={`${formatTime(c.started_at)} 开始，${c.stopped_at ? `${formatTime(c.stopped_at)} 手动停止` : `${formatTime(c.expires_at)} 到期`}；已抓 ${c.packets ?? "—"} / ${c.max_packets} 个 PDU（北京时间）。${c.note ? `备注：${c.note}` : ""}`}
        actions={<>
          {back}
          <Button onClick={() => window.location.assign(captures.pcapURL(c.id))}>下载 pcap</Button>
          {canWrite && c.state === "running" && <Button variant="danger" onClick={() => setStopping(true)}>停止</Button>}
        </>} />
      {packets.error && <Alert tone="danger" title="读取抓包数据失败">{packets.error}</Alert>}
      <Toolbar right={c.state === "running" && <ToolbarCount>每 2 秒刷新</ToolbarCount>}>
        <TextInput size="sm" className="ax-search" placeholder="搜索命令、状态、号码或内容" aria-label="搜索" value={q} onChange={(e) => setQ(e.target.value)} />
      </Toolbar>
      <TableWrap loading={packets.loading}>
        {shown.length ? (
          <>
            <Table>
              <thead><tr><Th w={136}>时间</Th><Th w={88}>方向</Th><Th w={200}>对端</Th><Th w={168}>命令</Th><Th numeric w={88}>seq</Th><Th w={176}>状态</Th><Th numeric w={72}>字节</Th><Th w={360}>摘要</Th><Th /></tr></thead>
              <tbody>
                {paged.rows.map((p) => (
                  <tr key={p.id}>
                    <td className="ax-mono">{formatClock(p.time)}</td>
                    <td>{p.out ? "发出 →" : "← 收到"}</td>
                    <td className="ax-mono" title={`${p.instance} ${p.local}`}>{p.remote}</td>
                    <td className="ax-mono">{p.command}</td>
                    <td className="ax-num">{p.seq}</td>
                    <td className="ax-mono">{p.status ? <Pill tone="danger" plain>{p.status_name}</Pill> : p.status_name}</td>
                    <td className="ax-num">{p.length}</td>
                    <td className="ax-mono ax-secondary">{pduSummary(p)}</td>
                    <td><Button variant="ghost" size="sm" onClick={() => setOpen(p)}>详情</Button></td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <Pager {...paged} />
          </>
        ) : (
          !packets.loading && <EmptyState title={keyword ? "没有匹配的 PDU" : "还没有抓到 PDU"}>{keyword || c.state !== "running" ? "" : "会话上有收发时这里每 2 秒刷新；没有流量时只有 enquire_link。"}</EmptyState>
        )}
      </TableWrap>
      {open && <PDUDrawer p={open} onClose={() => setOpen(null)} />}
      {stopping && <StopModal capture={c} name={target(c)} onClose={() => setStopping(false)} />}
    </>
  );
}

/** 一个 PDU 的解码字段与十六进制原文。 */
function PDUDrawer({ p, onClose }: { p: CapturedPDU; onClose: () => void }) {
  return (
    <Drawer open title={<><span className="ax-mono">{p.command}</span> seq {p.seq}</>} onClose={onClose}>
      <KeyValue columns={2} items={[
        { label: "时间", value: `${formatTime(p.time)} ${formatClock(p.time)}`, mono: true },
        { label: "方向", value: p.out ? "网关发出" : "网关收到" },
        { label: "本端", value: `${p.local}（${p.instance}）`, mono: true },
        { label: "对端", value: p.remote, mono: true },
        { label: "system_id", value: p.system_id, mono: true },
        { label: "command_status", value: `${p.status_name}（0x${p.status.toString(16).padStart(8, "0")}）`, mono: true },
      ]} />
      {p.error && <Alert tone="warning" title="正文解析失败">{p.error}</Alert>}
      {p.fields.length > 0 && (
        <>
          <SectionTitle>字段</SectionTitle>
          <KeyValue items={p.fields.map((f) => ({ label: f.name, value: f.value || "—", mono: true }))} />
        </>
      )}
      <SectionTitle>原文（{p.length} 字节）</SectionTitle>
      <ReadonlyBlock label="十六进制原文" value={hexDump(p.hex)} />
    </Drawer>
  );
}

/** 按游标把任务的 PDU 全部拉下来并追加，loading 是第一轮还没拉完；live 时每 2 秒再拉一次新增的，任务结束后最后拉一次就停。一次最多 1000 个，拉满就接着拉。 */
function usePackets(id: number, live: boolean) {
  const [items, setItems] = useState<CapturedPDU[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [pulled, setPulled] = useState(0); // 第一轮拉完的任务；没拉完时表格显示加载动画
  const cursor = useRef("");
  const loaded = useRef(0); // 已拉的是哪个任务：换了任务就从头拉
  useEffect(() => {
    if (!id) return;
    if (loaded.current !== id) {
      loaded.current = id;
      cursor.current = "";
      setItems([]);
    }
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const pull = async () => {
      try {
        for (;;) {
          const page = await captures.packets(id, cursor.current);
          if (stopped) return;
          if (page.items.length) {
            cursor.current = page.next;
            setItems((prev) => [...prev, ...page.items]);
          }
          if (page.items.length < 1000) break;
        }
        setError(null);
      } catch (e) {
        if (!stopped) setError(errorMessage(e));
      }
      if (!stopped) setPulled(id);
      if (!stopped && live) timer = setTimeout(pull, 2_000);
    };
    void pull();
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, [id, live]);
  return { items, error, loading: id > 0 && pulled !== id };
}
